import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { Link } from "@tanstack/react-router";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ConnectorFlowsUnified from "@/components/marketing/visuals/ConnectorFlowsUnified";
import { MobileStickyCta } from "@/components/marketing/MobileStickyCta";

export const Route = createFileRoute("/connectors/product")({
  component: ConnectorProductPage,
});

const SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Larasoft Connectors",
  description:
    "Secure, bi-directional connectors that synchronise cloud solutions and your own database. Prebuilt integrations, custom sources, and rapid onboarding for new and existing customers.",
  url: "https://larasoft.global/connectors/product",
  provider: {
    "@type": "Organization",
    name: "Larasoft",
    url: "https://larasoft.global",
  },
};

const METRICS = [
  { label: "Success rate", value: "99%" },
  { label: "Records/hour", value: "50k+" },
  { label: "Prebuilt connectors", value: "20+" },
  { label: "Monitoring", value: "24/7" },
];

const FEATURES = [
  {
    title: "Proven synchronisation",
    copy: "Reliable, scheduled data movement between cloud solutions and your database - built on battle-tested pipelines that handle thousands of records daily.",
  },
  {
    title: "Your own cloud database",
    copy: "Data lands in your own cloud-hosted database, giving your team direct SQL access for reporting, analysis, and integration with tools like Power BI and Tableau.",
  },
  {
    title: "Logical tables for reporting",
    copy: "Connector data is organised into clean, logical tables designed for ease of reporting - no manual transformation or spreadsheet stitching required.",
  },
  {
    title: "Bi-directional cloud integrations",
    copy: "Anything-to-anything cloud connectivity. Push data from one cloud solution into another and pull it back again - true two-way synchronisation between the cloud platforms your organisation relies on.",
  },
  {
    title: "Custom connectors",
    copy: "Beyond our prebuilt library, we build custom connectors from sources like CSV, FTP, SFTP, Excel, XML, and bespoke APIs - whatever your data source, we can connect it.",
  },
  {
    title: "Encrypted & access-controlled",
    copy: "All data is encrypted at rest and in transit. Role-based permissions ensure only authorised users can view or manage connector configurations.",
  },
];

const INTEGRATION_EXAMPLES = [
  {
    logo: "/xero-central-logo-primary-version.svg",
    logoAlt: "Xero",
    title: "Xero + Anything",
    description:
      "Bi-directional APIs move transactions between mainstream cloud solutions and/or custom platforms and Xero with reconciliations handled automatically.",
  },
  {
    logo: "/Larasoft Logo-portrait.svg",
    logoAlt: "Larasoft",
    title: "Point of sale to finance",
    description:
      "GAAP and other POS feeds push daily sales, inventory, and tender detail into Xero without manual uploads or duplicated effort.",
  },
  {
    logo: "/Larasoft Logo-portrait.svg",
    logoAlt: "Larasoft",
    title: "CSV & Excel to Xero",
    description:
      "Structured flat-file imports convert CSV or Excel templates into balanced journals that stay aligned with your source data.",
  },
];

const CONNECTOR_CATEGORIES = [
  {
    label: "Finance & ERP",
    items: [
      "Xero",
      "Cin7 (Dear)",
      "Power Office",
      "Uniconta",
      "GAAP",
      "GAAP Unity",
    ],
  },
  {
    label: "Retail & Commerce",
    items: ["WooCommerce", "Vend (Lightspeed)", "Mailchimp"],
  },
  {
    label: "Payroll & Time Tracking",
    items: ["PaySpace Payroll", "SimplePay", "Harvest"],
  },
  {
    label: "Healthcare",
    items: ["MedicalDirector Helix"],
  },
  {
    label: "Operations",
    items: ["Wherehouse", "OData", "Webhook"],
  },
  {
    label: "General Purpose",
    items: [
      "Microsoft SQL Server",
      "Excel",
      "CSV",
      "XML",
      "FTP",
      "SFTP",
      "Zoho CRM",
    ],
  },
  {
    label: "Analytics",
    items: ["Power BI", "Tableau", "Microsoft Excel"],
  },
];

function ConnectorProductPage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>Connectors - cloud data pipelines | Larasoft</title>
        <meta
          name="description"
          content="Secure, bi-directional connectors that synchronise cloud solutions and your own database. Prebuilt integrations, custom sources, and rapid onboarding for new and existing customers."
        />
        <link
          rel="canonical"
          href="https://larasoft.global/connectors/product"
        />
        <script type="application/ld+json">
          {JSON.stringify(SERVICE_SCHEMA)}
        </script>
      </Helmet>

      {/* Hero */}
      <Section
        containerClassName="grid grid-cols-1 items-center gap-8 md:grid-cols-[2fr_3fr]"
        className="py-20"
      >
        <div className="space-y-6">
          <Badge className="w-max" variant="info">
            Connectors
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-brand-blue-dark">
            Connect anything to anything - your data, your database
          </h1>
          <p className="text-slate-600 max-w-xl">
            Pre-built and custom connectors synchronise cloud solutions with your
            own cloud-hosted database. Rapid onboarding means new connectors can
            be added for existing and new customers in days, not months.
          </p>

          <div className="flex flex-wrap gap-3">
            <Button asChild variant="secondary">
              <Link to="/contact">Request a trial</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/contact">Talk to sales</Link>
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4 text-xs text-slate-600 sm:grid-cols-4">
            {METRICS.map((metric) => (
              <div
                key={metric.label}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
              >
                <div className="text-sm font-semibold text-slate-900">
                  {metric.value}
                </div>
                <div>{metric.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl">
          <ConnectorFlowsUnified />
        </div>
      </Section>

      <MobileStickyCta />

      {/* Rapid addition callout */}
      <Section containerClassName="space-y-4" className="py-12">
        <Card className="border-brand-blue-dark/20 bg-brand-blue-dark text-white shadow-lg">
          <CardContent className="space-y-3 p-6 sm:p-10">
            <CardTitle className="text-2xl font-bold text-white sm:text-3xl">
              Rapid addition of connectors
            </CardTitle>
            <CardDescription className="text-base text-white/90 max-w-3xl sm:text-lg">
              Need a new integration? Our connector framework lets us stand up
              new data pipelines quickly for both existing and new customers.
              Whether you're connecting a mainstream cloud platform or a niche
              internal cloud system, we get you live fast - with the same proven
              reliability and monitoring as every other connector in our library.
            </CardDescription>
          </CardContent>
        </Card>
      </Section>

      {/* Features */}
      <Section containerClassName="space-y-8" className="py-16">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-brand-blue-dark">Features</h2>
          <p className="text-sm text-slate-600">
            Everything you need for reliable, secure data movement between your
            cloud solutions and your database.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature) => (
            <Card key={feature.title}>
              <CardContent className="space-y-3">
                <CardTitle>{feature.title}</CardTitle>
                <CardDescription>{feature.copy}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Integration examples */}
      <Section containerClassName="space-y-6" className="py-16">
        <div className="space-y-3">
          <Badge
            variant="info"
            className="w-max uppercase tracking-[0.2em] text-xs"
          >
            Cloud-to-cloud integrations
          </Badge>
          <h2 className="text-2xl font-bold sm:text-3xl text-brand-blue-dark">
            Stop capturing your data twice
          </h2>
          <p className="max-w-3xl text-slate-600">
            Managed integrations keep cloud apps, point-of-sale platforms, and
            finance tools synchronised - so teams stop relying on brittle
            spreadsheet workflows.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {INTEGRATION_EXAMPLES.map((example) => (
            <Card
              key={example.title}
              className="border-white/60 bg-white/70 shadow-sm backdrop-blur"
            >
              <CardContent className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-20 w-20 items-center justify-center">
                    <img
                      src={example.logo}
                      alt={example.logoAlt}
                      className="h-18 w-18 object-contain"
                    />
                  </span>
                  <CardTitle className="m-0">{example.title}</CardTitle>
                </div>
                <CardDescription>
                  <span className="font-semibold text-brand-blue-dark">
                    {example.title}
                  </span>{" "}
                  - {example.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Case study */}
      <Section containerClassName="space-y-6" className="py-16">
        <div className="space-y-2">
          <Badge
            variant="info"
            className="w-max uppercase tracking-[0.2em] text-xs"
          >
            Case Study
          </Badge>
          <h2 className="text-2xl font-bold sm:text-3xl text-brand-blue-dark">
            Trusted by organisations that depend on accurate data
          </h2>
        </div>
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="grid grid-cols-1 items-center gap-8 p-6 sm:p-10 md:grid-cols-[auto_1fr]">
            <img
              src="/uturn-logo.png"
              alt="U-Turn Homeless Ministries"
              className="h-24 w-auto object-contain"
            />
            <div className="space-y-3">
              <CardTitle>U-Turn Homeless Ministries</CardTitle>
              <CardDescription className="max-w-2xl">
                U-Turn accelerated donor engagement by automating real-time
                Xero to Salesforce CRM synchronisation - eliminating duplicate
                data entry between their accounting and donor management teams.
              </CardDescription>
              <Button asChild variant="outline" size="sm">
                <Link to="/connectors/xero-case-study">Read the full story</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* Connector catalogue */}
      <Section containerClassName="space-y-6" className="py-16">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-brand-blue-dark">Prebuilt connector catalogue</h2>
          <p className="text-sm text-slate-600 max-w-2xl">
            Connect to the tools you already use. Our growing library of prebuilt
            connectors covers finance, retail, payroll, and general-purpose data
            sources - with new integrations added regularly.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CONNECTOR_CATEGORIES.map((category) => (
            <Card key={category.label}>
              <CardContent className="space-y-3">
                <CardTitle className="text-base">{category.label}</CardTitle>
                <div className="flex flex-wrap gap-2">
                  {category.items.map((item) => (
                    <Badge key={item} variant="outline" className="text-xs">
                      {item}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 pt-2">
          <Button asChild variant="secondary">
            <Link to="/contact">Request the full connector list</Link>
          </Button>
        </div>
      </Section>
    </MarketingLayout>
  );
}
